import type { ReactElement, ReactNode } from "react";
import { SHAPE_CLASS } from "../../lib/shape.js";

/* ---------------- ChartTooltip ----------------
   The hover readout of a recharts chart: the hovered category, then one row
   per series. Pass it as `content={<ChartTooltip />}`; recharts clones it with
   `active`, `label` and `payload`. */

/** A series reading as recharts hands it over (its own `ValueType`). */
export type ChartTooltipValue = number | string | readonly (number | string)[];

/** One series row of the hovered category, as recharts hands it over. */
export interface ChartTooltipEntry {
  readonly name?: string | number | undefined;
  /** `null` or absent: the series has no reading here. */
  readonly value?: ChartTooltipValue | null | undefined;
  /** The series colour recharts copies from its stroke or fill. Feed series
   *  colours from token variables (`hsl(var(--pb-cyan))`). */
  readonly color?: string | undefined;
}

export interface ChartTooltipProps {
  /** Set by recharts: the pointer is over a data point. */
  readonly active?: boolean | undefined;
  /** Set by recharts: the hovered category. */
  readonly label?: string | number | undefined;
  /** Set by recharts: one entry per series. */
  readonly payload?: readonly ChartTooltipEntry[] | undefined;
  /** Prints a reading. Default: the value as text. */
  readonly formatValue?: (value: ChartTooltipValue, name: string) => ReactNode;
  /** Printed for a series with no reading. Default "No data". */
  readonly missingLabel?: string;
  readonly "data-testid"?: string;
}

/**
 * The shared chart hover readout. It owns its markup, so a missing reading
 * prints `missingLabel` rather than an empty row, and the panel paints with
 * token classes rather than recharts' inline `contentStyle`.
 *
 * recharts' `filterNull` DEFAULTS TO TRUE and strips null series from the
 * payload before this runs: a chart that wants `missingLabel` rows must pass
 * `filterNull={false}` to its `Tooltip`.
 *
 * It takes no colour prop. The one inline colour is each row's swatch, read
 * from the payload entry. It spreads no rest props, because recharts clones it
 * with every Tooltip prop and those are not DOM attributes. Decorative by
 * construction: recharts renders it inside the chart's hidden subtree, so the
 * accessible reading is the consumer's table.
 */
export function ChartTooltip({
  active,
  label,
  payload,
  formatValue,
  missingLabel = "No data",
  "data-testid": testId,
}: ChartTooltipProps): ReactElement | null {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null;
  }
  return (
    <div
      data-testid={testId}
      className={`${SHAPE_CLASS.rounded} border border-pb-border bg-pb-card px-3 py-2 text-xs text-pb-text shadow-md`}
    >
      <p className="font-semibold text-pb-text">{String(label ?? "")}</p>
      <ul className="mt-1 space-y-0.5">
        {payload.map((entry, index) => {
          const name = String(entry.name ?? "");
          const value = entry.value;
          return (
            <li
              key={`${String(index)}-${name}`}
              className="flex items-center gap-2"
            >
              <span
                aria-hidden="true"
                className="h-0.5 w-3 shrink-0 rounded-full"
                style={{ backgroundColor: entry.color ?? "currentColor" }}
              />
              <span className="text-pb-text-muted">{name}</span>
              <span className="ms-auto pb-tabular">
                {value === null || value === undefined
                  ? missingLabel
                  : formatValue !== undefined
                    ? formatValue(value, name)
                    : String(value)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
